import { Accordion } from '@cabonline/ui-web';
import { useLocale } from '@multiplatform-apps/service-trips';
import { serializeError } from '../../utils';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { StyledErrorDetails } from './styles';
import { ErrorDetailsAccordionProps } from './types';

export const ErrorDetailsAccordion = ({
  error,
}: ErrorDetailsAccordionProps): ReactElement => {
  const { t } = useLocale();
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const errorDetails = useMemo(() => serializeError(error), [error]);

  const handleCopyDetails = useCallback((): void => {
    try {
      navigator.clipboard.writeText(errorDetails).then(() => {
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }
        setIsCopied(true);
        copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
      }).catch(() => {
        // clipboard write was rejected — silently degrade
      });
    } catch {
      // clipboard API unavailable (non-secure context) — silently degrade
    }
  }, [errorDetails]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Accordion
      isExpanded={isDetailsExpanded}
      setIsExpanded={setIsDetailsExpanded}
      title={t('error.details.title')}
      headerStyle="outline"
      actionButton={{
        label: isCopied
          ? t('error.details.copied')
          : t('error.details.copyButton'),
        onClick: handleCopyDetails,
      }}
    >
      <StyledErrorDetails>{errorDetails}</StyledErrorDetails>
    </Accordion>
  );
};
